import { openDB, type DBSchema } from 'idb';

interface OfflineChunk {
    key: string; // "bookId/filename"
    bookId: string;
    filename: string;
    size: number; // bytes
    timestamp: number; // for LRU
    pinned: boolean; // true = user downloaded, false = smart cache
    smart: boolean; // true = smart cache (can be both pinned and smart? No, pinned overrides smart for eviction purposes)
}

interface OfflineDB extends DBSchema {
    chunks: {
        key: string;
        value: OfflineChunk;
        indexes: { 'by-book': string, 'by-time': number };
    };
}

const DB_NAME = 'corpora-offline-store';
const STORE_NAME = 'chunks';

class OfflineStore {
    private dbPromise;
    private root: FileSystemDirectoryHandle | null = null;
    private opfsSupported = false;

    constructor() {
        this.dbPromise = openDB<OfflineDB>(DB_NAME, 1, {
            upgrade(db) {
                const store = db.createObjectStore(STORE_NAME, { keyPath: 'key' });
                store.createIndex('by-book', 'bookId');
                store.createIndex('by-time', 'timestamp');
            },
        });

        // Init OPFS
        if (typeof navigator !== 'undefined' && navigator.storage && navigator.storage.getDirectory) {
            this.opfsSupported = true;
            navigator.storage.getDirectory().then(r => this.root = r).catch(e => {
                console.error("OPFS Access Failed", e);
                this.opfsSupported = false;
            });
        }
    }

    private getKey(bookId: string, filename: string) {
        return `${bookId}/${filename}`;
    }

    /**
     * Save a chunk to storage.
     * Uses OPFS if available, otherwise falls back to indexedDB (not implemented here purely for blobs to keep size down, but we could).
     * Actually fallback to IDB for blobs is tricky with size limits. 
     * For this implementation, we will strictly require OPFS for the blob storage to avoid janking the main thread with massive IDB reads/writes.
     * If OPFS is not supported, we just don't cache.
     */
    async saveChunk(bookId: string, filename: string, blob: Blob, type: 'smart' | 'pinned') {
        if (!this.opfsSupported) return;
        const root = await this.getRoot();
        if (!root) return;

        try {
            // 1. Write Blob
            const dir = await root.getDirectoryHandle(bookId, { create: true });
            const fileHandle = await dir.getFileHandle(filename, { create: true });
            // @ts-ignore - TS types for OPFS createWritable might be missing in some setups
            const writable = await fileHandle.createWritable();
            await writable.write(blob);
            await writable.close();

            // 2. Update Metadata
            const db = await this.dbPromise;
            const key = this.getKey(bookId, filename);

            // Check existing to preserve 'pinned' status if we are just smart-caching update
            const existing = await db.get(STORE_NAME, key);

            const isPinned = type === 'pinned' || (existing?.pinned || false);
            const isSmart = type === 'smart' || (existing?.smart || false); // If it was smart, it stays smart? Not necessarily.
            // If explicit download, it is pinned. If it was already pinned, it stays pinned.
            // If smart cache, it sets smart=true. 

            await db.put(STORE_NAME, {
                key,
                bookId,
                filename,
                size: blob.size,
                timestamp: Date.now(),
                pinned: isPinned,
                smart: true // Everything in this store effectively is smart-cacheable unless strictly pinned-only. But let's say 'smart' flag means "counted against smart quota"
                // Actually, if pinned, it shouldn't count against smart quota.
                // Refined logic:
                // If type='pinned', pinned=true, smart=false (doesn't count to eviction)
                // If type='smart' AND NOT already pinned: pinned=false, smart=true
                // If type='smart' AND already pinned: pinned=true, smart=false (it's pinned, so we ignore smart eviction)
            });

            // If we are saving as 'pinned', we explicitly mark it so it survives eviction.
            // If we are saving as 'smart', we timestamp it.

        } catch (e) {
            console.error("Failed to save chunk", e);
        }
    }

    async getChunk(bookId: string, filename: string): Promise<Blob | null> {
        if (!this.opfsSupported) return null;
        const root = await this.getRoot();
        if (!root) return null;

        try {
            const dir = await root.getDirectoryHandle(bookId);
            const fileHandle = await dir.getFileHandle(filename);
            const file = await fileHandle.getFile();

            // Update timestamp for LRU if it's a smart cache item
            // We do this asynchronously to not block
            this.touchTimestamp(bookId, filename);

            return file;
        } catch {
            return null;
        }
    }

    async touchTimestamp(bookId: string, filename: string) {
        const db = await this.dbPromise;
        const key = this.getKey(bookId, filename);
        const item = await db.get(STORE_NAME, key);
        if (item && !item.pinned) { // Only update LRU for non-pinned
            item.timestamp = Date.now();
            db.put(STORE_NAME, item);
        }
    }

    async deleteChunk(bookId: string, filename: string) {
        if (!this.opfsSupported) return;
        const root = await this.getRoot();
        if (!root) return;

        try {
            const dir = await root.getDirectoryHandle(bookId);
            await dir.removeEntry(filename);

            const db = await this.dbPromise;
            await db.delete(STORE_NAME, this.getKey(bookId, filename));
        } catch (e) {
            // Ignore if not found
        }
    }

    // Deletes all chunks for a book (used for removing offline download)
    async removeBook(bookId: string) {
        if (!this.opfsSupported) return;
        const root = await this.getRoot();
        if (!root) return;

        try {
            await root.removeEntry(bookId, { recursive: true });
        } catch { /* ignore */ }

        // Cleanup DB
        const db = await this.dbPromise;
        const tx = db.transaction(STORE_NAME, 'readwrite');
        const index = tx.store.index('by-book');
        let cursor = await index.openCursor(IDBKeyRange.only(bookId));
        while (cursor) {
            await cursor.delete();
            cursor = await cursor.continue();
        }
        await tx.done;
    }

    /**
     * Enforce Smart Cache Quota (LRU Eviction).
     * @param quotaMB Limit in Megabytes
     */
    async enforceQuota(quotaMB: number) {
        const quotaBytes = quotaMB * 1024 * 1024;
        const db = await this.dbPromise;

        // Get all SMART items (not pinned)
        const all = await db.getAll(STORE_NAME);
        const smartItems = all.filter(x => !x.pinned && x.smart);

        // Sort by timestamp asc (oldest first)
        smartItems.sort((a, b) => a.timestamp - b.timestamp);

        // Calculate total size
        let currentSize = smartItems.reduce((acc, x) => acc + x.size, 0);

        if (currentSize <= quotaBytes) return; // Under quota

        // Evict
        const toDelete: OfflineChunk[] = [];
        for (const item of smartItems) {
            if (currentSize <= quotaBytes) break;
            toDelete.push(item);
            currentSize -= item.size;
        }

        // Perform deletions
        for (const item of toDelete) {
            await this.deleteChunk(item.bookId, item.filename);
        }

        console.log(`[OfflineStore] Evicted ${toDelete.length} chunks to fit quota.`);
    }

    async getStats() {
        const db = await this.dbPromise;
        const all = await db.getAll(STORE_NAME);

        let smartSize = 0;
        let pinnedSize = 0;
        let smartCount = 0;
        let pinnedCount = 0;

        for (const x of all) {
            if (x.pinned) {
                pinnedSize += x.size;
                pinnedCount++;
            } else if (x.smart) {
                smartSize += x.size;
                smartCount++;
            }
        }

        return {
            smart: { size: smartSize, count: smartCount },
            pinned: { size: pinnedSize, count: pinnedCount },
            totalSize: smartSize + pinnedSize
        };
    }

    async debugListChunks(limit = 20) {
        const db = await this.dbPromise;
        const all = await db.getAll(STORE_NAME);
        // Sort by time desc (newest first)
        all.sort((a, b) => b.timestamp - a.timestamp);
        return all.slice(0, limit).map(x => ({
            key: x.key,
            size: x.size,
            pinned: x.pinned,
            smart: x.smart,
            timestamp: x.timestamp
        }));
    }

    async getAllBookStats() {
        const db = await this.dbPromise;
        const all = await db.getAll(STORE_NAME);
        const map = new Map<string, number>();
        all.forEach(chunk => {
            const current = map.get(chunk.bookId) || 0;
            map.set(chunk.bookId, current + chunk.size);
        });
        return Array.from(map.entries()).map(([bookId, totalBytes]) => ({ bookId, totalBytes }));
    }

    async clearSmartCache() {
        const db = await this.dbPromise;
        const all = await db.getAll(STORE_NAME);
        const smart = all.filter(x => !x.pinned && x.smart);

        for (const item of smart) {
            await this.deleteChunk(item.bookId, item.filename);
        }
    }

    async isBookDownloaded(bookId: string): Promise<boolean> {
        // Naive: check if any pinned chunks exist for book. 
        // Ideally we should track "Download Completed" state separately, but checking for >0 chunks is a decent proxy for "started".
        // Better: The UI will track state. This is just low-level.
        const db = await this.dbPromise;
        const index = db.transaction(STORE_NAME).store.index('by-book');
        const chunks = await index.getAll(IDBKeyRange.only(bookId));
        return chunks.some(c => c.pinned);
    }

    // Check persistence
    async checkPersistence() {
        if (navigator.storage && navigator.storage.persisted) {
            return await navigator.storage.persisted();
        }
        return false;
    }

    async requestPersistence() {
        if (navigator.storage && navigator.storage.persist) {
            return await navigator.storage.persist();
        }
        return false;
    }

    private async getRoot() {
        if (this.root) return this.root;
        if (typeof navigator !== 'undefined' && navigator.storage && navigator.storage.getDirectory) {
            this.root = await navigator.storage.getDirectory();
            return this.root;
        }
        return null;
    }
}

export const offlineStore = new OfflineStore();
